
import PaginationHelper from '@/utils/PaginationHelper';
import { Vue, Component } from 'vue-property-decorator';
import { List, HeaderList, Data } from '@/components/Table/TableModel';
import {
  CustomSelectOption,
  CustomSelectModel
} from '@/components/CustomSelect/CustomSelectModel';
import flagKeys from '@/constants/flagKeys';

@Component({})
export default class BasePagination extends Vue {
  dataList: List = new List([], []);
  flagKeys = flagKeys;
  pageName!: string;
  subTitle: string = 'subTitle';
  labelInputFilter: string = 'labelInputFilter';
  search: string = '';
  debounceToSearch: any = '';
  debounceRefreshByScrool: any = '';
  sortField: string = '';
  sortOrder: string = '';
  maxItems: boolean = false;
  skip: number = 0;
  limit: number = 20;
  pagination!: string;
  query: string = '';
  refreshing: boolean = false;
  filters: CustomSelectOption[] = [];
  isLoading: boolean = false;
  filterModel: any = [];
  listFiltersSelecteds: any = '';
  helper: PaginationHelper = new PaginationHelper();
  labelToFilter: string = '';
  apiExtraKey: string = '';
  searchableFields: string = '';
  filterModels: CustomSelectModel[] = [];
  hasLoadedMore: boolean = false;
  repeatForEachValue: boolean = false;
  filterValuesSeparator: string = ',';
  disableBindEvents = false;
  targetClassForInfineScroll = '.page';

  reloadList?(
    clearList?: boolean,
    refreshCurrent?: boolean,
    query?: string
  ): Promise<any> | Data | void;
  initHeader?(): HeaderList[] | void;
  initFilterModel?(): void;
  detail?($event: any): void;

  created() {
    this.middleInitHeader();
    this.resetPagination();
    this.middleReloadList(true);
    if (this.initFilterModel) this.initFilterModel();
  }

  mounted() {
    this.bindEvents();
  }

  beforeDestroy() {
    this.unbindEvents();
  }

  async middleReloadList(...props: any) {
    if (this.reloadList) {
      const list = await this.reloadList(...props);
      if (typeof list !== 'undefined') {
        this.dataList.list = list;
      }
    }
  }

  middleInitHeader() {
    if (this.initHeader) {
      const header = this.initHeader();
      if (typeof header !== 'undefined') {
        this.dataList.header = header;
      }
    }
  }

  filterBySearch(value: string, gaCategory?: string) {
    this.search = value;
    this.maxItems = false;
    this.resetPagination();
    clearTimeout(this.debounceToSearch);
    this.debounceToSearch = setTimeout(() => {
      this.filterByStatus(this.filters);

      if (gaCategory) {
        this.$analytics({
          eventCategory: gaCategory,
          eventAction: 'change',
          eventLabel: `Searched term: "${value}"`
        });
      }
    }, 800);
  }

  filterChanged(filters: string[]) {
    this.resetPagination();
    if (!filters) return;
    const { search } = this;
    const newsFilterSelecteds = this.helper.filterChanged(filters, search);
    this.listFiltersSelecteds = newsFilterSelecteds;
  }

  addSearch(value: string) {
    this.search = value;
    this.maxItems = false;
    this.resetPagination();
    clearTimeout(this.debounceToSearch);
    this.debounceToSearch = setTimeout(() => {
      if (this.query.includes('searchableFields')) {
        const oldSearch = this.helper.getSearchByQuery(
          this.query,
          this.searchableFields
        );
        this.query = this.query.replace(
          oldSearch,
          `&${this.searchableFields}&search=${value}`
        );
      } else {
        this.query += `&${this.searchableFields}&search=${value}`;
      }

      if (this.listFiltersSelecteds) {
        const newsFilterSelecteds = JSON.parse(
          JSON.stringify(this.listFiltersSelecteds)
        );
        newsFilterSelecteds.title = [value];
        this.listFiltersSelecteds = newsFilterSelecteds;
      } else {
        this.listFiltersSelecteds = {
          status: [],
          title: [value]
        };
      }

      this.middleReloadList(true, true);
    }, 500);
  }

  addFilter(
    param: string,
    value: string,
    fields: CustomSelectOption[],
    model: CustomSelectModel
  ) {
    let transformedFields = fields;

    const uuids = fields.map(field => field.uuid);

    if (uuids.includes('hiddenOnApp') && uuids.includes('visibleOnApp')) {
      transformedFields = fields.filter(
        field => !['hiddenOnApp', 'visibleOnApp'].includes(field.uuid)
      );
    }

    if (!this.query) {
      this.labelToFilter = param;
      this.apiExtraKey = value;
      this.filterByStatus(transformedFields);
      return;
    }

    const filterValuesSeparator = this.filterValuesSeparator;
    const selectedFields = transformedFields.filter(field => field.isSelected);
    const mapFields = selectedFields.map(field =>
      this.repeatForEachValue ? `${value}:${field.uuid}` : field.uuid
    );

    if (this.query.includes(value)) {
      const oldFields = this.helper.getFieldsByQuery(this.query, value);
      const newFields = mapFields;
      this.query = this.query.replace(
        oldFields.join(filterValuesSeparator),
        newFields.join(filterValuesSeparator)
      );
      if (!selectedFields.length) {
        this.query = this.query.replace(`${value}:`, '');
      }
    } else {
      const uuids = mapFields;
      const queryValue = this.repeatForEachValue
        ? uuids.join(filterValuesSeparator)
        : `${value}:${uuids.join(filterValuesSeparator)}`;
      this.query += `&${param}=${queryValue}`;
    }

    //Clear param if there's no value
    if (this.query.split('&').find(params => params === `${param}=`)) {
      this.query = this.query.replace(`&${param}=`, '');
    }

    const newsFilterSelecteds = JSON.parse(
      JSON.stringify(this.listFiltersSelecteds)
    );

    //Remove all filters from this model
    const groupLabels = model.groups[0].options.map(option => option.label);
    newsFilterSelecteds.status = newsFilterSelecteds.status.filter(
      (filter: string) => !groupLabels.includes(filter)
    );

    //Add new filters
    const newFilters = selectedFields.map(field => field.label);
    newFilters.forEach(filter => {
      if (!newsFilterSelecteds.status.includes(filter)) {
        newsFilterSelecteds.status.push(filter);
      }
    });

    this.maxItems = false;
    this.resetPagination();
    this.listFiltersSelecteds = newsFilterSelecteds;
    this.middleReloadList(true, true);
  }

  multiFilterRemoved(filter: string) {
    let value: string = filter.toLowerCase();
    let hasFound: boolean = false;

    // Disable option in filter models
    this.filterModels.forEach((filterModel: CustomSelectModel, i: number) => {
      const { options } = filterModel.groups[0];
      const labels = options.map(option => option.label);
      if (labels.includes(filter)) {
        const optionIndex = options.findIndex(
          option => option.label === filter
        );
        this.filterModels[i].groups[0].options[optionIndex].isSelected = false;
        value = this.filterModels[i].groups[0].options[optionIndex].uuid;
        hasFound = true;
      }
    });

    if (!hasFound) {
      // Is a search value
      const index = this.query.search('searchableFields');
      const subtrQuery = this.query.substr(index);
      const auxIndex = subtrQuery.search('search=');
      const endIndex =
        subtrQuery.substr(auxIndex).search('&') > 0
          ? subtrQuery.substr(auxIndex).search('&') + auxIndex
          : subtrQuery.length;
      const oldSearch = subtrQuery.slice(0, endIndex);

      this.query = this.query.replace(oldSearch, '');
      this.listFiltersSelecteds.title = [];
      this.search = '';
    } else {
      // Is a filter value
      this.query = this.query.replace(value, '');
    }

    //Sanitaze query
    this.query = this.query.replace(/,,/g, ',').replace(/:,/g, ':');

    this.maxItems = false;
    this.resetPagination();
    this.middleReloadList(true, true);
  }

  filterByStatus(props: CustomSelectOption[]) {
    const { search, labelToFilter, apiExtraKey, searchableFields } = this;
    const {
      modelFiltersSelected,
      nameFilterSelected,
      query
    } = this.helper.filterByStatus(
      props,
      search,
      labelToFilter,
      apiExtraKey,
      searchableFields,
      this.filterValuesSeparator,
      this.repeatForEachValue
    );

    this.maxItems = false;
    this.filters = modelFiltersSelected;
    this.query = query;
    this.filterChanged(nameFilterSelected);
    this.middleReloadList(true, true);
  }

  filterByStatusAndType(props: CustomSelectOption[]) {
    const { search, labelToFilter, apiExtraKey, searchableFields } = this;
    const {
      modelFiltersSelected,
      nameFilterSelected
    } = this.helper.filterByStatus(
      props,
      search,
      labelToFilter,
      apiExtraKey,
      searchableFields,
      this.filterValuesSeparator,
      this.repeatForEachValue
    );

    const queryParams = new URLSearchParams();

    const filterTypes = ['general', 'birthday'];

    const filterTypeOptions = modelFiltersSelected
      .filter(option => filterTypes.includes(option.uuid))
      .map(option => option.uuid)
      .join(',');

    const filterStatusOptions = modelFiltersSelected
      .filter(option => !filterTypes.includes(option.uuid))
      .map(option => option.uuid)
      .join(',');

    if (filterTypeOptions) {
      queryParams.append('type', filterTypeOptions);
    }

    if (filterStatusOptions) {
      queryParams.append('status', filterStatusOptions);
    }

    const finalQueryParam = `&${decodeURIComponent(queryParams.toString())}`;

    this.maxItems = false;
    this.filters = modelFiltersSelected;
    this.query = finalQueryParam;
    this.filterChanged(nameFilterSelected);
    this.middleReloadList(true, true);
  }

  filterRemoved(filter: string) {
    const {
      newFilters,
      newNamesFilterSelected,
      newTitle
    } = this.helper.filterRemoved(
      filter,
      this.listFiltersSelecteds.status,
      this.filterModel.groups[0].options,
      this.search
    );

    this.search = newTitle;
    this.listFiltersSelecteds.status = newNamesFilterSelected;
    this.filterByStatus(newFilters);
  }

  // function with relation in pagination
  sortChanged(field: string, { gaCategory = '', headers = [] } = {}) {
    this.maxItems = false;
    if (field === this.sortField) {
      this.sortOrder = this.sortOrder === 'ASC' ? 'DESC' : 'ASC';
    } else {
      this.sortField = field;
      this.sortOrder = 'DESC';
    }
    this.resetPagination();
    this.middleReloadList(false, true);

    if (gaCategory) {
      const column = headers.find(
        (header: HeaderList) => field === header.field
      ) as HeaderList | undefined;

      this.$analytics({
        eventCategory: gaCategory,
        eventAction: 'click',
        eventLabel: `[${this.sortOrder}] "${
          column ? column.name : field
        }" column`
      });
    }
  }

  loadMore() {
    if (!this.pagination) return;
    clearTimeout(this.debounceRefreshByScrool);
    this.debounceRefreshByScrool = setTimeout(() => {
      this.hasLoadedMore = true;
      this.middleReloadList();
    }, 300);
  }

  resetPagination() {
    this.skip = 0;
    this.pagination = this.helper.firstPagination(this.skip, this.limit);
  }

  bindEvents() {
    if (this.disableBindEvents) return;
    const container: HTMLElement | null = document.querySelector(
      this.targetClassForInfineScroll
    );
    if (container) container.addEventListener('scroll', this.handleScroll);
  }

  unbindEvents() {
    if (this.disableBindEvents) return;
    const container: HTMLElement | null = document.querySelector(
      this.targetClassForInfineScroll
    );
    if (container) container.removeEventListener('scroll', this.handleScroll);
  }

  handleScroll() {
    const element = document.querySelector(this.targetClassForInfineScroll);

    if (element) {
      if (
        element.scrollTop + element.clientHeight + 40 >=
        element.scrollHeight
      ) {
        this.loadMore();
      }
    }
  }

  get getFlags() {
    return this.$store.getters['flags/getFlags'];
  }

  verifyFlag(flag: string) {
    return this.getFlags.includes(flag);
  }
}
